import React from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Mail, MessageSquare, Paperclip, Eye } from 'lucide-react';
import { formatCurrency } from '../lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getStatusBadge = (status) => {
  const colors = {
    'Pending': 'bg-yellow-100 text-yellow-800', 'Under Review': 'bg-blue-100 text-blue-800',
    'Approved': 'bg-green-100 text-green-800', 'Rejected': 'bg-red-100 text-red-800',
    'Declined': 'bg-red-100 text-red-800', 'Killed': 'bg-gray-800 text-white',
    'On Hold': 'bg-orange-100 text-orange-800', 'More Info Needed': 'bg-purple-100 text-purple-800',
  };
  return <Badge className={colors[status] || 'bg-gray-100'}>{status}</Badge>;
};

export const ReviewDialog = ({ open, onOpenChange, selectedApp, reviewAction, reviewData, setReviewData, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{reviewAction === 'approve' ? 'Approve Application' : 'Reject Application'}</DialogTitle>
      </DialogHeader>
      {selectedApp && (
        <div className="space-y-4">
          <div><p className="text-sm text-gray-600">Business</p><p className="font-medium">{selectedApp.business_name}</p></div>
          <div><p className="text-sm text-gray-600">Requested Amount</p><p className="font-bold text-lg">{formatCurrency(selectedApp.requested_amount)}</p></div>
          {reviewAction === 'reject' && (
            <div>
              <Label htmlFor="rejection_reason">Rejection Reason *</Label>
              <Textarea id="rejection_reason" rows={4} value={reviewData.rejection_reason} onChange={(e) => setReviewData({ ...reviewData, rejection_reason: e.target.value })} placeholder="Provide a reason for rejection..." />
            </div>
          )}
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={onSubmit} className={reviewAction === 'reject' ? 'bg-red-600 hover:bg-red-700' : ''}>{reviewAction === 'approve' ? 'Approve' : 'Reject'}</Button>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export const ConvertDialog = ({ open, onOpenChange, selectedApp, reviewData, setReviewData, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl">
      <DialogHeader><DialogTitle>Convert to Deal</DialogTitle></DialogHeader>
      {selectedApp && (
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-md">
            <p className="text-sm text-gray-600">Business</p><p className="font-bold">{selectedApp.business_name}</p>
            <p className="text-sm text-gray-600 mt-2">Requested Amount</p><p className="font-bold text-lg">{formatCurrency(selectedApp.requested_amount)}</p>
          </div>
          <div><h3 className="font-semibold mb-3">Deal Terms</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Approved Amount *</Label><Input type="number" step="0.01" value={reviewData.approved_amount} onChange={(e) => setReviewData({ ...reviewData, approved_amount: e.target.value })} placeholder="e.g., 50000" /></div>
              <div><Label>Factor Rate *</Label><Input type="number" step="0.01" value={reviewData.factor_rate} onChange={(e) => setReviewData({ ...reviewData, factor_rate: e.target.value })} placeholder="e.g., 1.35" /></div>
              <div><Label>Term (Days) *</Label><Input type="number" value={reviewData.term_days} onChange={(e) => setReviewData({ ...reviewData, term_days: e.target.value })} placeholder="e.g., 120" /></div>
              <div><Label>Commission Rate (%)</Label><Input type="number" step="0.01" value={reviewData.commission_rate} onChange={(e) => setReviewData({ ...reviewData, commission_rate: e.target.value })} placeholder="e.g., 10" /></div>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={onSubmit} disabled={!reviewData.approved_amount || !reviewData.factor_rate || !reviewData.term_days}>Convert to Deal</Button>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export const EditDialog = ({ open, onOpenChange, selectedApp, editFormData, setEditFormData, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle>Edit Application</DialogTitle></DialogHeader>
      {selectedApp && (
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold mb-3">Business Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Business Name *</Label><Input value={editFormData.business_name} onChange={(e) => setEditFormData({...editFormData, business_name: e.target.value})} /></div>
              <div><Label>DBA</Label><Input value={editFormData.dba} onChange={(e) => setEditFormData({...editFormData, dba: e.target.value})} /></div>
              <div><Label>EIN / Tax ID</Label><Input value={editFormData.ein_tax_id} onChange={(e) => setEditFormData({...editFormData, ein_tax_id: e.target.value})} /></div>
              <div><Label>Business Type</Label><Select value={editFormData.business_type} onValueChange={(v) => setEditFormData({...editFormData, business_type: v})}><SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger><SelectContent><SelectItem value="LLC">LLC</SelectItem><SelectItem value="Corporation">Corporation</SelectItem><SelectItem value="Sole Proprietor">Sole Proprietor</SelectItem><SelectItem value="Partnership">Partnership</SelectItem></SelectContent></Select></div>
              <div><Label>Industry</Label><Input value={editFormData.industry} onChange={(e) => setEditFormData({...editFormData, industry: e.target.value})} /></div>
              <div><Label>Years in Business</Label><Input type="number" step="0.1" value={editFormData.years_in_business} onChange={(e) => setEditFormData({...editFormData, years_in_business: e.target.value})} /></div>
              <div><Label>Monthly Revenue</Label><Input type="number" step="0.01" value={editFormData.monthly_revenue} onChange={(e) => setEditFormData({...editFormData, monthly_revenue: e.target.value})} /></div>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-3">Owner Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Owner Name *</Label><Input value={editFormData.owner_name} onChange={(e) => setEditFormData({...editFormData, owner_name: e.target.value})} /></div>
              <div><Label>Phone</Label><Input value={editFormData.owner_phone} onChange={(e) => setEditFormData({...editFormData, owner_phone: e.target.value})} /></div>
              <div><Label>Email</Label><Input type="email" value={editFormData.owner_email} onChange={(e) => setEditFormData({...editFormData, owner_email: e.target.value})} /></div>
              <div className="col-span-2"><Label>Address</Label><Input value={editFormData.owner_address} onChange={(e) => setEditFormData({...editFormData, owner_address: e.target.value})} /></div>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-3">Deal Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Requested Amount *</Label><Input type="number" step="0.01" value={editFormData.requested_amount} onChange={(e) => setEditFormData({...editFormData, requested_amount: e.target.value})} /></div>
              <div><Label>Purpose of Funds</Label><Input value={editFormData.purpose_of_funds} onChange={(e) => setEditFormData({...editFormData, purpose_of_funds: e.target.value})} /></div>
            </div>
          </div>
          <div><Label>Additional Notes</Label><Textarea rows={3} value={editFormData.notes} onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})} /></div>
          <div className="flex justify-end space-x-2"><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={onSubmit}>Save Changes</Button></div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export const StatusDialog = ({ open, onOpenChange, selectedApp, newStatus, setNewStatus, statusNotes, setStatusNotes, sendEmailNotification, setSendEmailNotification, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader><DialogTitle>Change Application Status</DialogTitle></DialogHeader>
      {selectedApp && (
        <div className="space-y-4">
          <div><p className="text-sm text-gray-600">Business</p><p className="font-medium">{selectedApp.business_name}</p></div>
          <div><p className="text-sm text-gray-600">Current Status</p><div className="mt-1">{getStatusBadge(selectedApp.status)}</div></div>
          <div><Label>New Status *</Label><Select value={newStatus} onValueChange={setNewStatus}><SelectTrigger><SelectValue placeholder="Select new status" /></SelectTrigger><SelectContent><SelectItem value="Pending">Pending</SelectItem><SelectItem value="Under Review">Under Review</SelectItem><SelectItem value="Approved">Approved</SelectItem><SelectItem value="Declined">Declined</SelectItem><SelectItem value="Killed">Killed</SelectItem><SelectItem value="On Hold">On Hold</SelectItem><SelectItem value="More Info Needed">More Info Needed</SelectItem></SelectContent></Select></div>
          <div><Label>Status Change Notes</Label><Textarea rows={4} value={statusNotes} onChange={(e) => setStatusNotes(e.target.value)} placeholder="Add notes about this status change..." /></div>
          <div className={`p-3 rounded-md ${sendEmailNotification ? 'bg-blue-50' : 'bg-gray-50'}`}>
            <div className="flex items-start space-x-3">
              <input type="checkbox" id="sendEmail" checked={sendEmailNotification} onChange={(e) => setSendEmailNotification(e.target.checked)} className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <label htmlFor="sendEmail" className="flex-1 cursor-pointer">
                <div className="flex items-center space-x-2"><Mail className={`h-5 w-5 ${sendEmailNotification ? 'text-blue-600' : 'text-gray-400'}`} /><span className={`text-sm font-medium ${sendEmailNotification ? 'text-blue-900' : 'text-gray-600'}`}>Send Email Notification</span></div>
                <p className={`text-sm mt-1 ${sendEmailNotification ? 'text-blue-800' : 'text-gray-500'}`}>{sendEmailNotification ? `An email will be sent to ${selectedApp.owner_email || 'the applicant'} notifying them of this status change.` : 'No email will be sent. Status will be updated silently.'}</p>
              </label>
            </div>
          </div>
          <div className="flex justify-end space-x-2"><Button variant="outline" onClick={() => { onOpenChange(false); setSendEmailNotification(true); }}>Cancel</Button><Button onClick={onSubmit} disabled={!newStatus || newStatus === selectedApp.status}>{sendEmailNotification ? 'Change Status & Send Email' : 'Change Status'}</Button></div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export const NoteDialog = ({ open, onOpenChange, selectedApp, noteContent, setNoteContent, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl">
      <DialogHeader><DialogTitle>Application Notes</DialogTitle></DialogHeader>
      {selectedApp && (
        <div className="space-y-4">
          <div className="bg-gray-50 p-3 rounded-md"><p className="text-sm text-gray-600">Business</p><p className="font-medium">{selectedApp.business_name}</p></div>
          {selectedApp.underwriter_notes?.length > 0 && (
            <div className="space-y-3"><Label>Previous Notes</Label>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {selectedApp.underwriter_notes.map((note, idx) => (
                  <div key={note.id || idx} className="bg-blue-50 p-3 rounded-md border border-blue-100">
                    <div className="flex justify-between items-start"><p className="text-sm font-medium text-blue-900">{note.user_name}</p><p className="text-xs text-gray-500">{new Date(note.created_at).toLocaleString()}</p></div>
                    <p className="text-sm text-gray-700 mt-1">{note.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div><Label>Add New Note</Label><Textarea rows={4} value={noteContent} onChange={(e) => setNoteContent(e.target.value)} placeholder="Enter your note about this application..." /></div>
          <div className="flex justify-end space-x-2"><Button variant="outline" onClick={() => { onOpenChange(false); setNoteContent(''); }}>Close</Button><Button onClick={onSubmit} disabled={!noteContent.trim()}><MessageSquare className="h-4 w-4 mr-2" />Add Note</Button></div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export const DocumentsDialog = ({ open, onOpenChange, selectedApp }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl">
      <DialogHeader><DialogTitle>Application Documents</DialogTitle></DialogHeader>
      {selectedApp && (
        <div className="space-y-4">
          <div className="bg-gray-50 p-3 rounded-md"><p className="text-sm text-gray-600">Business</p><p className="font-medium">{selectedApp.business_name}</p></div>
          {selectedApp.application_documents?.length > 0 ? (
            <div className="space-y-3">
              {selectedApp.application_documents.map((doc, idx) => (
                <div key={doc.id || idx} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                  <div className="flex items-center gap-3"><Paperclip className="h-5 w-5 text-gray-400" /><div><p className="font-medium">{doc.file_name}</p><p className="text-sm text-gray-500">{doc.document_type}</p></div></div>
                  <Button size="sm" variant="outline" onClick={() => window.open(`${BACKEND_URL}${doc.file_url}`, '_blank')}><Eye className="h-4 w-4 mr-1" />View</Button>
                </div>
              ))}
            </div>
          ) : <p className="text-center text-gray-500 py-8">No documents attached to this application</p>}
          <div className="flex justify-end"><Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button></div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);
